import Ws from '@adonisjs/websocket-client'
import { apiDomain } from '../config'

const EVENTS = [
  'job:snapshot', 'job:started', 'job:progress', 'job:log', 'job:finished',
  'job:update', 'job:config', 'job:cleanup', 'job:error', 'job:activity',
  'pipeline:snapshot', 'pipeline:started', 'pipeline:step', 'pipeline:progress',
  'pipeline:log', 'pipeline:finished', 'pipeline:update', 'pipeline:config', 'pipeline:error',
  'audience:snapshot', 'audience:update', 'audience:error',
  'content:snapshot', 'content:updated', 'content:bulk', 'content:error',
  'onboarding:snapshot', 'onboarding:card', 'onboarding:activity', 'onboarding:status', 'onboarding:error'
]

function socketBaseUrl () {
  const fallback = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const url = new URL(apiDomain || '/api', fallback)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = url.pathname.replace(/\/api\/?$/i, '') || '/'
  url.search = ''
  url.hash = ''
  return url.toString().replace(/\/$/, '')
}

function closeSubscription (sub) {
  if (!sub) return
  if (typeof sub.close === 'function') sub.close()
  else if (typeof sub.unsubscribe === 'function') sub.unsubscribe()
}

class Realtime {
  constructor () {
    this.ws = null
    this.subscriptions = {}
    this.listeners = {}
    this.connected = false
    this.stateHandlers = []
    this.lastError = null
  }

  connect () {
    if (this.ws) return
    const token = localStorage.getItem('usertoken') || localStorage.usertoken
    if (!token) return

    try {
      const ws = Ws(socketBaseUrl(), {
        path: 'adonis-ws',
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000
      })
      this.ws = ws
      ws.on('open', () => {
        this.connected = true
        this.lastError = null
        Object.keys(this.listeners).forEach((topic) => this._join(topic))
        this._emitState('open')
      })
      ws.on('close', () => {
        this.connected = false
        this.subscriptions = {}
        this._emitState('close')
      })
      ws.on('error', (error) => {
        this.connected = false
        this.lastError = error || new Error('WebSocket connection failed.')
        this._emitState('error', this.lastError)
      })
      ws.withJwtToken(token).connect()
    } catch (e) {
      this.ws = null
      this.connected = false
      this.lastError = e
      this._emitState('error', e)
    }
  }

  isConnected () {
    return !!this.connected
  }

  isSubscribed (topic) {
    const sub = this.subscriptions[topic]
    return this.isConnected() && !!sub && sub.state === 'open'
  }

  errorMessage (error) {
    const detail = error || this.lastError
    if (!detail) return 'Live updates are unavailable; using periodic refresh.'
    return detail.message || detail.reason ||
      (detail.error && (detail.error.message || detail.error.reason)) ||
      'Live updates are unavailable; using periodic refresh.'
  }

  subscribe (topic, handlers) {
    if (!topic || !handlers) return () => {}
    if (!this.listeners[topic]) this.listeners[topic] = []
    const entry = { handlers }
    this.listeners[topic].push(entry)

    this.connect()
    if (this.connected) this._join(topic)

    return () => {
      const set = this.listeners[topic]
      if (!set) return
      this.listeners[topic] = set.filter((item) => item !== entry)
      if (!this.listeners[topic].length) {
        delete this.listeners[topic]
        closeSubscription(this.subscriptions[topic])
        delete this.subscriptions[topic]
      }
    }
  }

  refresh (topic) {
    // A rejected/closed topic can be retried even while the connection stays up.
    if (this.connected && this.listeners[topic]) this._join(topic)
    const sub = this.subscriptions[topic]
    if (sub && typeof sub.emit === 'function') sub.emit('refresh', {})
  }

  emit (topic, event, payload) {
    const sub = this.subscriptions[topic]
    if (sub && typeof sub.emit === 'function') sub.emit(event, payload || {})
  }

  onStateChange (fn) {
    if (typeof fn !== 'function') return () => {}
    this.stateHandlers.push(fn)
    return () => {
      this.stateHandlers = this.stateHandlers.filter((handler) => handler !== fn)
    }
  }

  _join (topic) {
    if (!this.ws || this.subscriptions[topic]) return
    let sub = null
    try {
      sub = this.ws.getSubscription(topic) || this.ws.subscribe(topic)
    } catch (e) {
      sub = this.ws.getSubscription(topic)
    }
    if (!sub) return

    this.subscriptions[topic] = sub
    sub.on('ready', () => this._emitState('ready', { topic }))
    sub.on('close', () => {
      if (this.subscriptions[topic] === sub) delete this.subscriptions[topic]
      this._emitState('subscription:close', { topic })
    })
    sub.on('error', (error) => {
      this.lastError = error || new Error(`WebSocket subscription failed for ${topic}.`)
      this._emitState('error', this.lastError)
    })

    EVENTS.forEach((event) => {
      sub.on(event, (data) => this._dispatch(topic, event, data))
    })
  }

  _dispatch (topic, event, data) {
    const set = this.listeners[topic]
    if (!set) return
    set.slice().forEach((entry) => {
      const handlers = entry.handlers || {}
      if (typeof handlers[event] === 'function') handlers[event](data)
      if (typeof handlers['*'] === 'function') handlers['*'](event, data)
    })
  }

  _emitState (state, detail) {
    this.stateHandlers.slice().forEach((fn) => fn(state, this.connected, detail || null))
  }

  disconnect () {
    Object.keys(this.subscriptions).forEach((topic) => closeSubscription(this.subscriptions[topic]))
    this.subscriptions = {}
    if (this.ws) {
      try { this.ws.close() } catch (e) {
        // Best-effort disconnect during logout/navigation.
      }
    }
    this.ws = null
    this.connected = false
    this.lastError = null
    this._emitState('close')
  }
}

export default new Realtime()
